import type { ReactNode } from 'react';

/**
 * Chart primitives ported from the kit (D4: SVG/CSS, no chart library).
 * Brand-first series order --admin-chart-1…6, brown-tinted grid lines, no
 * gradient fills.
 */

export const CHART_COLORS = [
  'var(--admin-chart-1)',
  'var(--admin-chart-2)',
  'var(--admin-chart-3)',
  'var(--admin-chart-4)',
  'var(--admin-chart-5)',
  'var(--admin-chart-6)',
] as const;

/** Kit `Bar` — labelled horizontal proportion row (comparisons, funnels, leaderboards). */
export function Bar({
  label,
  value,
  pct,
  color = CHART_COLORS[0],
  labelWidth = 150,
}: {
  label: ReactNode;
  value: ReactNode;
  /** 0–100. */
  pct: number;
  color?: string;
  labelWidth?: number;
}) {
  const width = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-center gap-3 font-body text-[13px]">
      <div className="shrink-0 truncate text-ink" style={{ width: labelWidth }}>
        {label}
      </div>
      <div
        className="h-2 flex-1 overflow-hidden rounded-pill bg-[rgba(143,68,32,.08)]"
        role="meter"
        aria-valuenow={Math.round(width)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-full rounded-pill" style={{ width: `${width}%`, background: color }} />
      </div>
      <div className="w-14 text-right text-muted tabular-nums">{value}</div>
    </div>
  );
}

/**
 * Kit `PairedBars` — grouped vertical bars (e.g. published vs sold per day).
 * Series `a` is chart-1 (the latest point full strength, earlier ones at .6),
 * series `b` is chart-2 at .65. Four horizontal grid lines.
 *
 * `independentScales` sizes each series against its own maximum — for two
 * series in different units (listings vs portions) that must not share an
 * axis. Tooltips always show the raw values.
 */
export function PairedBars({
  a,
  b,
  labels,
  height = 150,
  aLabel,
  bLabel,
  independentScales = false,
}: {
  a: number[];
  b: number[];
  labels: string[];
  height?: number;
  aLabel?: string;
  bLabel?: string;
  independentScales?: boolean;
}) {
  const shared = Math.max(1, ...a, ...b) * 1.12;
  const aMax = independentScales ? Math.max(1, ...a) * 1.12 : shared;
  const bMax = independentScales ? Math.max(1, ...b) * 1.12 : shared;
  return (
    <div
      className={`relative flex items-end pt-2 font-body ${a.length > 20 ? 'gap-px' : a.length > 12 ? 'gap-1' : 'gap-2'}`}
      role="img"
      aria-label={[aLabel, bLabel].filter(Boolean).join(' vs ')}
    >
      {[0.25, 0.5, 0.75, 1].map((p) => (
        <div
          key={p}
          aria-hidden="true"
          className="absolute inset-x-0 border-t border-[rgba(143,68,32,.1)]"
          style={{ bottom: `${p * (height / (height + 22)) * 100}%` }}
        />
      ))}
      {a.map((v, i) => (
        <div key={i} className="relative z-[1] flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <div className="flex w-full items-end gap-0.5" style={{ height }}>
            <div
              title={aLabel ? `${aLabel}: ${v}` : String(v)}
              className="flex-1 rounded-t"
              style={{
                height: `${(v / aMax) * 100}%`,
                background: i === a.length - 1 ? 'var(--admin-chart-1)' : 'rgba(236,109,45,.6)',
              }}
            />
            <div
              title={bLabel ? `${bLabel}: ${b[i] ?? 0}` : String(b[i] ?? 0)}
              className="flex-1 rounded-t bg-[rgba(127,150,254,.65)]"
              style={{ height: `${((b[i] ?? 0) / bMax) * 100}%` }}
            />
          </div>
          {/* Zero-width box: a label may overhang its column without widening it. */}
          <div className="flex h-3.5 w-0 justify-center overflow-visible">
            <span className="whitespace-nowrap text-[10px] text-muted">{labels[i]}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Kit `Legend` — swatch + label pairs. */
export function Legend({ items }: { items: Array<[label: string, color: string]> }) {
  return (
    <div className="flex flex-wrap gap-4 font-body text-xs text-muted">
      {items.map(([label, color]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-[9px] w-[9px] rounded-sm" style={{ background: color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

/** Kit `Trend` — a tiny bar sparkline (last point full strength). */
export function Sparkline({ series, title }: { series: number[]; title?: (v: number) => string }) {
  const max = Math.max(1, ...series);
  return (
    <span className="inline-flex h-[22px] items-end gap-[3px]" role="img" aria-label="trend">
      {series.map((v, i) => (
        <span
          key={i}
          title={title ? title(v) : String(v)}
          className="inline-block w-[7px] rounded-sm"
          style={{
            height: `${Math.max((v / max) * 22, 2)}px`,
            background: i === series.length - 1 ? 'var(--admin-chart-1)' : 'rgba(236,109,45,.45)',
          }}
        />
      ))}
    </span>
  );
}
